import type {KeyboardEvent} from 'react';
import React, {useEffect, useId, useRef, useState} from 'react';

import {Button, Coachmark} from '@albert-io/atomic';
import {session} from 'lib/StorageUtil';

import {useAnnotationModeStore} from '../store/AnnotationModeStore';
import {MARK_SELECTION_HINT} from '../constants';

const SEEN_KEY_PREFIX = 'annotation-mode-coachmark-seen:';

const TITLE = 'Answering is disabled';

const BODY = `Placing and submitting guesses are disabled while highlighting. ${MARK_SELECTION_HINT}`;

// The caret's tip sits 7px past the card's edge; this keeps it off the round toggle.
const CARET_CLEARANCE = 8;

const claimedWithoutStorage = new Set<string>();

/** Whether session storage has `key` down as seen; storage that can't be read has nothing. */
const isStoredSeen = (key: string): boolean => {
  try {
    return session.get(key, false) === true;
  } catch {
    return false;
  }
};

/**
 * Claims the coachmark's one showing for `scopeKey` in this tab. Where session storage
 * can't hold the claim, it lasts until the page reloads; a missing key never claims.
 */
const claimShowing = (scopeKey: string | null): boolean => {
  if (!scopeKey) {
    return true;
  }
  const key = `${SEEN_KEY_PREFIX}${scopeKey}`;
  if (claimedWithoutStorage.has(key) || isStoredSeen(key)) {
    return false;
  }
  session.set(key, true);
  if (!isStoredSeen(key)) {
    claimedWithoutStorage.add(key);
  }
  return true;
};

interface AnnotationModeCoachmarkOptions {
  /** The assignment or practice being worked; the coachmark shows once per key per tab. */
  scopeKey: string | null;
  /** Something else that opened with the mode is up; the coachmark waits for it to close. */
  isHeldBack?: boolean;
}

/**
 * When the toggle turns the tool on, tells the student once that answering waits until
 * it is off again. `offer` goes in the toggle's click handler; turning the tool off
 * retires a showing for good.
 */
export const useAnnotationModeCoachmark = ({
  scopeKey,
  isHeldBack = false
}: AnnotationModeCoachmarkOptions): {
  /** The toggle to point at while the coachmark shows; null otherwise. */
  target: HTMLElement | null;
  offer: (toggle: HTMLElement | null) => void;
  dismiss: () => void;
} => {
  const isAnnotationModeOn = useAnnotationModeStore((state) => state.isAnnotationModeOn);
  const [toggle, setToggle] = useState<HTMLElement | null>(null);
  if (!isAnnotationModeOn && toggle) {
    setToggle(null);
  }

  const offer = (element: HTMLElement | null) => {
    if (!isAnnotationModeOn && element && claimShowing(scopeKey)) {
      setToggle(element);
    }
  };

  const dismiss = () => {
    toggle?.focus();
    setToggle(null);
  };

  return {target: isAnnotationModeOn && !isHeldBack ? toggle : null, offer, dismiss};
};

interface AnnotationModeCoachmarkProps {
  target: HTMLElement;
  onDismiss: () => void;
}

/** The coachmark itself, hung below the annotation-mode toggle. */
export const AnnotationModeCoachmark = ({target, onDismiss}: AnnotationModeCoachmarkProps) => {
  const bodyId = useId();
  const dismissRef = useRef<HTMLButtonElement>(null);

  // A modal closing in the same commit (the Heads up notice) hands focus back to the toggle
  // a microtask later; take it again, but never from anything the student has moved to.
  useEffect(() => {
    const timer = setTimeout(() => {
      if (document.activeElement === target || document.activeElement === document.body) {
        dismissRef.current?.focus();
      }
    });
    return () => clearTimeout(timer);
  }, [target]);

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key !== 'Escape') {
      return;
    }
    // Answered here, so the document-level Escape leaves the mode on.
    event.preventDefault();
    onDismiss();
  };

  return (
    <Coachmark
      body={<span id={bodyId}>{BODY}</span>}
      data-testid='annotation-mode-coachmark'
      offset={CARET_CLEARANCE}
      placement='bottom-end'
      target={target}
      title={TITLE}
      // The student can start marking straight away, and the style editor needs focus.
      trapFocus={false}
    >
      <Button
        ref={dismissRef}
        aria-describedby={bodyId}
        autoFocus
        color='blend-dark'
        data-testid='annotation-mode-coachmark__dismiss-btn'
        onClick={onDismiss}
        onKeyDown={handleKeyDown}
        size='s'
      >
        Got It
      </Button>
    </Coachmark>
  );
};
